import Input from "../../components/Input";
import { createInvoice, GetStInvNum } from "../../actions/studensts";
import { getInvoiceNumber } from "../../actions/studensts";
import ActionButtonForward from "./ActionButtonsForward";
import ActionButtons from "./ActionButtons";
import AlertDenger from "../../components/AlertDenger";
import AlertSuccess from "../../components/AlertSuccess";
import Loading from "../../components/Loading";
import { useEffect, useState } from "react";

const exam_name = [
  "ACT1 One Trial Certificate",
  "ACT1 Super Score Certificate",
  "ACT1 Matching Certificate",
];

const initialValue = {
  exam_name: exam_name[0],
  month: "",
  year: "",
  act1: false,
  act1_copy_number: 0,
  // Nearly every invoice includes the ACT1 certificate itself, so it starts
  // on "Need One"; the extra copies stay at 0 until someone asks for them.
  act1Crt: 1,
  act2: false,
  act2Crt: 0,
  act2_month: "",
  act2_year: "",
  subject: "",
  act2_copy_number: 0,
  top_achievers: false,
  rush: false,
  admin_fees: 0,
  shipping: 0,
  moe_auth: false,
  moe1_copy_number: 0,
  moe1_number: 0,
  moe2_copy_number: 0,
  moe2_number: 0,
  // Separate MOHE Statement invoice (flat 600 EGP); not part of the main total.
  mohe_statement: false,
  mohe_statement_count: 1,
  act2_id: "",
  act1_try: "",
  act2_try: "",
  discount: 0.0,
  invoice_type: "",
  company_name: "American Academy",
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Dates arrive as 'YYYY-MM-DD' strings (the pool runs with dateStrings), so
// they sort chronologically as plain strings.
const toIsoDate = (value) => {
  if (!value) return null;
  const text = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
};

const latestDate = (values) => {
  const dates = values.map(toIsoDate).filter(Boolean).sort();
  return dates.length ? dates[dates.length - 1] : null;
};

const monthYearOf = (isoDate) => {
  if (!isoDate) return { month: "", year: "" };
  const [year, month] = isoDate.split("-");
  return { month: MONTH_NAMES[Number(month) - 1] || "", year };
};

// Join the per-trial years, listing each distinct year only once in order:
// "2025 - 2025" collapses to "2025", and "2022 - 2025 - 2025" to "2022 - 2025".
// The months still list every trial, so they no longer align 1:1 with the
// years — that is intended, a repeated year is shown a single time.
const joinYears = (years) => [...new Set(years)].join(" - ");

// Categories that carry a MOE authentication fee (the priceMap entries with a
// non-zero `moe` in Server/src/utils/pricing.js). MOE Auth is defaulted on only
// for these.
const MOE_CATEGORIES = ["individual", "individualA", "foreign", "foreignA", "foreignA_UAE", "foreignB_UAE"];

// An ACT ID is "real" only when it points at an actual exam. The submission
// form leaves it as 0 / "0" for a student who did not sit that exam, and an
// ACT2 (subject) ID is often simply null — both, plus the string forms those
// can serialize into ("null", "0.0", empty/whitespace), mean "no ID".
const hasRealId = (value) => {
  if (value === null || value === undefined) return false;
  const text = String(value).trim().toLowerCase();
  if (text === "" || text === "null" || text === "undefined") return false;
  // Treat any purely-numeric-zero form (0, 0.0, 00) as absent.
  return !/^0*(\.0+)?$/.test(text);
};

// Carry over what the student submitted in the audit form so the invoice
// clerk doesn't retype it. Only the ACT1 / ACT2 blocks are inherited, and the
// copies numbers are deliberately left at their 0 default rather than
// inherited, since every copy is an extra charge. MOE authentication stays a
// deliberate choice here for the same reason.
// Month/Year come from the *latest* trial date, which is the one the
// certificate is issued for on multi-trial (Super Score) students.
function inheritFromStudent(student) {
  if (!student) return {};

  const inherited = {};

  // --- ACT1 ---
  const act1Scores = Array.isArray(student.act1_scores) ? student.act1_scores : [];
  const act1TryDates = [1, 2, 3, 4, 5].map((i) => student[`act1_try_date${i}`]);
  const act1Dates = [...act1TryDates, ...act1Scores.map((s) => s.trial_date)];

  // A zero / missing ACT1 ID means the student has no ACT1 exam: leave the
  // section unticked and its certificate on "No Need" (overriding the default
  // act1Crt of 1).
  if (!hasRealId(student.act1_id)) {
    inherited.act1 = false;
    inherited.act1Crt = 0;
  } else {
    inherited.act1 = true;

    if (exam_name.includes(student.exam_name)) {
      inherited.exam_name = student.exam_name;
    }

    // A Super Score certificate lists every trial the student sat. The month
    // and year come from the subject_date in act1_exam_scores (falling back to
    // trial_date, then the try_dateN columns, for older data), and the trial
    // count follows from those dates. Two subjects sat the same day are one
    // trial, so identical dates are de-duplicated. Month and year stay
    // position-aligned per trial, like the ACT2 block, because the certificate
    // keeps them in separate cells.
    const isSuperScore = student.exam_name === "ACT1 Super Score Certificate";
    const act1SubjectDates = act1Scores.map((s) => s.subject_date);
    const act1ScoreTrialDates = act1Scores.map((s) => s.trial_date);
    const act1SourceDates = act1SubjectDates.some(Boolean)
      ? act1SubjectDates
      : act1ScoreTrialDates.some(Boolean)
      ? act1ScoreTrialDates
      : act1TryDates;
    const act1TrialDates = [
      ...new Set(act1SourceDates.map(toIsoDate).filter(Boolean)),
    ].sort();

    if (isSuperScore && act1TrialDates.length > 0) {
      inherited.act1_try = String(act1TrialDates.length);
      const parts = act1TrialDates.map(monthYearOf);
      inherited.month = parts.map((p) => p.month).join(" - ");
      inherited.year = joinYears(parts.map((p) => p.year));
    } else {
      // Other ACT1 certificates are a single trial: count from the per-trial
      // score rows (or the try_dateN columns) and show the latest trial's date.
      const trialNumbers = new Set(
        act1Scores.map((s) => s.trial_no).filter((n) => n !== null && n !== undefined)
      );
      const act1Trials = Math.max(
        trialNumbers.size,
        act1TryDates.filter(Boolean).length
      );
      if (act1Trials > 0) inherited.act1_try = String(act1Trials);

      const { month, year } = monthYearOf(latestDate(act1Dates));
      if (month) inherited.month = month;
      if (year) inherited.year = year;
    }
  }

  // --- ACT2 ---
  // One trial per subject slot, so a slot counts when any of its three fields
  // was filled in.
  const act2Slots = [1, 2, 3].filter(
    (i) =>
      student[`subject${i}`] ||
      student[`act2_score${i}`] ||
      student[`act2_try_date${i}`]
  );
  const act2Dates = [1, 2, 3].map((i) => student[`act2_try_date${i}`]);

  // Same rule as ACT1: a zero / missing ACT2 ID with no subject data entered
  // means no ACT2 — leave it unticked and on "No Need".
  if (!hasRealId(student.act2_id) && act2Slots.length === 0) {
    inherited.act2 = false;
    inherited.act2Crt = 0;
  } else {
    inherited.act2 = true;
    // act2Crt is priced on its own, so it is only turned on for students who
    // actually sat ACT2.
    inherited.act2Crt = 1;

    if (hasRealId(student.act2_id)) inherited.act2_id = String(student.act2_id);
    if (act2Slots.length > 0) inherited.act2_try = String(act2Slots.length);

    const subjects = [...new Set(act2Slots.map((i) => student[`subject${i}`]).filter(Boolean))];
    if (subjects.length > 0) inherited.subject = subjects.join(" - ");

    // List the month and year of every ACT2 trial when there is more than one.
    // Two subjects sat on the same day are one trial, so identical dates are
    // de-duplicated (e.g. 2025-12-23 twice counts once). The certificate keeps
    // month and year in separate aligned cells, so they are joined the same way
    // and stay position-aligned per trial.
    const act2TrialDates = [...new Set(act2Dates.map(toIsoDate).filter(Boolean))].sort();
    if (act2TrialDates.length > 0) {
      const parts = act2TrialDates.map(monthYearOf);
      inherited.act2_month = parts.map((p) => p.month).join(" - ");
      inherited.act2_year = joinYears(parts.map((p) => p.year));
    }
  }

  // --- MOE Auth ---
  // For the categories that carry a MOE authentication fee, default MOE Auth on
  // with "Need One" for whichever certificate(s) the student is getting. The
  // other categories (outside*, foreignUsd) have no MOE charge, so they stay
  // off. Copies numbers are left at 0 — only the certificate itself defaults on.
  if (MOE_CATEGORIES.includes(student.category)) {
    if (inherited.act1) {
      inherited.moe_auth = true;
      inherited.moe1_number = 1;
    }
    if (inherited.act2) {
      inherited.moe_auth = true;
      inherited.moe2_number = 1;
    }
  }

  return inherited;
}

// Which section each field belongs to, so editing a field takes back the
// confirmation of the section it sits in.
const SECTION_OF_FIELD = {
  act1: "act1",
  exam_name: "act1",
  act1_try: "act1",
  month: "act1",
  year: "act1",
  act1Crt: "act1",
  act1_copy_number: "act1",
  act2: "act2",
  act2_id: "act2",
  act2_try: "act2",
  act2_month: "act2",
  act2_year: "act2",
  subject: "act2",
  act2Crt: "act2",
  act2_copy_number: "act2",
  moe_auth: "moe",
  moe1_number: "moe",
  moe1_copy_number: "moe",
  moe2_number: "moe",
  moe2_copy_number: "moe",
  top_achievers: "fees",
  rush: "fees",
  mohe_statement: "mohe_statement",
  mohe_statement_count: "mohe_statement",
  admin_fees: "fees",
  shipping: "fees",
  discount: "fees",
  company_name: "fees",
};

function ConfirmSection({ confirmed, onConfirm, label }) {
  return (
    <div className="flex items-center justify-end gap-2 mt-2 pt-2 border-t">
      {!confirmed && (
        <span className="text-xs text-gray-500">Check the data above, then confirm</span>
      )}
      <button
        type="button"
        onClick={onConfirm}
        disabled={confirmed}
        className={`py-1 px-3 rounded text-sm font-medium border ${
          confirmed
            ? "bg-green-100 text-green-800 border-green-300 cursor-default"
            : "bg-white text-blue-700 border-blue-300 hover:bg-blue-50"
        }`}
      >
        {confirmed ? "✓ Confirmed" : label}
      </button>
    </div>
  );
}

export default function Create({ id, student }) {
    const url = "https://auth.americanacademyeg.us/api/v2";
    // const url = "https://local.americanacademyeg.us/api/v2";
    // const url = "http://localhost:5000/api/v2";


  const [invoiceData, setInvoiceData] = useState(() => ({
    ...initialValue,
    ...inheritFromStudent(student),
    id,
  }));
  const [invoiceNum, setInvoiceNum] = useState(0);
  const [text, setText] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showDengerAlert, setShowDengerAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(false);
  // Each section has to be confirmed before the invoice can be created, so
  // the inherited audit data is never billed without being looked at.
  const [confirmed, setConfirmed] = useState({});

  const confirmSection = (section) =>
    setConfirmed((prev) => ({ ...prev, [section]: true }));

  // Only the sections actually selected ask for a confirmation — an unticked
  // ACT2 has no data to check. Fees & Company is always filled in, so it is
  // always confirmed.
  const requiredSections = [
    ...(invoiceData.act1 ? ["act1"] : []),
    ...(invoiceData.act2 ? ["act2"] : []),
    ...((invoiceData.act1 || invoiceData.act2) && invoiceData.moe_auth
      ? ["moe"]
      : []),
    ...(invoiceData.mohe_statement ? ["mohe_statement"] : []),
    "fees",
  ];
  const allConfirmed = requiredSections.every((section) => confirmed[section]);

  if (showSuccessAlert || showDengerAlert) {
    setTimeout(function () {
      setShowSuccessAlert(false);
      setShowDengerAlert(false);
      setLoading(false);
      if (showSuccessAlert) {
        setCreated(true);
      }
    }, 2000);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allConfirmed) return;
    setLoading(true);

    const res = await createInvoice(invoiceData);
    if (res.status === 200) {
      setShowSuccessAlert(true);
      setText(res.data.message);
    } else {
      setShowDengerAlert(true);
      setText(res.data.message);
    }
  };

  const handleInputChange = (e) => {
    const target = e.target;
    let value;
    if (target.type === "checkbox") {
      value = target.checked;
    } else if (target.type === "number") {
      value = parseFloat(target.value);
    } else {
      value = target.value;
    }

    const name = target.name;
    setInvoiceData({
      ...invoiceData,
      [name]: value,
      // Ticking MOHE Statement starts it at one statement.
      ...(name === "mohe_statement" && value ? { mohe_statement_count: 1 } : {}),
    });

    // Changing something makes the earlier confirmation stale.
    const section = SECTION_OF_FIELD[name];
    if (section) {
      setConfirmed((prev) =>
        prev[section] ? { ...prev, [section]: false } : prev
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const data = await GetStInvNum(id);
      if (data) {
        const invoice_number1 = data.invoice_number[0].invoice_number;
        if (invoice_number1 !== null) {
          setInvoiceNum(invoice_number1);
        } else {
          const res = await getInvoiceNumber();
          const invoice_number2 = res.data.invoice_number;
          setInvoiceNum(invoice_number2);
        }
      }
    };

    fetchData();
  }, [id]);

  return (
    <div>
      <form
        onSubmit={handleSubmit}
        className="p-2 border rounded-lg my-2 shadow"
      >
        <div className="w-full flex items-center justify-center mb-4">
          {showSuccessAlert && <AlertSuccess text={text} />}
          {showDengerAlert && <AlertDenger text={text} />}
          <h1 className="text-gray-900 font-medium text-lg">Invoice</h1>
        </div>
        <div className="text-center pb-4 text-lg">
          Invoice Number : {invoiceNum}
        </div>
        <div className="p-1 border rounded-lg">
          <Input
            type="checkbox"
            placeholder="ACT1"
            name="act1"
            required={false}
            onChange={handleInputChange}
            value={invoiceData.act1}
          />
          {invoiceData.act1 && (
            <>
              <div className="m-2 flex items-center justify-center">
                <label htmlFor="act_subject2" className="flex-auto">
                  <span className="text-red-600">*</span>
                  Exam Name:
                </label>
                <select
                  className="flex-initial p-2 ml-1 border rounded-xl focus:outline-blue-700"
                  onChange={handleInputChange}
                  name="exam_name"
                  value={invoiceData.exam_name}
                >
                  {exam_name.map((exam, i) => (
                    <option value={exam} key={i}>
                      {exam}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                placeholder="No. of Trials ACT1"
                type="text"
                name="act1_try"
                value={invoiceData.act1_try}
                onChange={handleInputChange}
              />
              <Input
                placeholder="Month"
                type="text"
                name="month"
                value={invoiceData.month}
                onChange={handleInputChange}
              />
              <Input
                placeholder="Year"
                type="text"
                name="year"
                value={invoiceData.year}
                onChange={handleInputChange}
              />
              <div className="flex justify-between">
                <p className="p-2 pr-6">ACT1 Crt Number: </p>
                <div className="flex">
                  <Input
                    placeholder="No Need"
                    type="radio"
                    name="act1Crt"
                    value="0"
                    checked={String(invoiceData.act1Crt) === "0"}
                    onChange={handleInputChange}
                  />
                  <Input
                    placeholder="Need One"
                    type="radio"
                    name="act1Crt"
                    value="1"
                    checked={String(invoiceData.act1Crt) === "1"}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <Input
                placeholder="ACT1 Copies Number"
                type="number"
                name="act1_copy_number"
                value={invoiceData.act1_copy_number}
                onChange={handleInputChange}
              />
              <ConfirmSection
                confirmed={confirmed.act1}
                onConfirm={() => confirmSection("act1")}
                label="Confirm ACT1 Data"
              />
            </>
          )}
        </div>
        <div className="mt-2 p-1 border rounded-lg">
          <Input
            type="checkbox"
            placeholder="ACT2 Subject"
            name="act2"
            required={false}
            onChange={handleInputChange}
            value={invoiceData.act2}
          />
          {invoiceData.act2 && (
            <>
              <Input
                placeholder="ACT2 ID/Email"
                type="text"
                name="act2_id"
                value={invoiceData.act2_id}
                onChange={handleInputChange}
                required={false}
              />
              <Input
                placeholder="No. of Trials ACT2"
                type="text"
                name="act2_try"
                value={invoiceData.act2_try}
                onChange={handleInputChange}
              />
              <Input
                placeholder="ACT2 Subject Month"
                type="text"
                name="act2_month"
                value={invoiceData.act2_month}
                onChange={handleInputChange}
              />
              <Input
                placeholder="ACT2 Subject Year"
                type="text"
                name="act2_year"
                value={invoiceData.act2_year}
                onChange={handleInputChange}
              />
              <Input
                placeholder="ACT2 Subject "
                type="text"
                name="subject"
                value={invoiceData.subject}
                onChange={handleInputChange}
              />
              <div className="flex justify-between">
                <p className="p-2 pr-6">ACT2 Crt Number: </p>
                <div className="flex">
                  <Input
                    placeholder="No Need"
                    type="radio"
                    name="act2Crt"
                    value="0"
                    checked={String(invoiceData.act2Crt) === "0"}
                    onChange={handleInputChange}
                  />
                  <Input
                    placeholder="Need One"
                    type="radio"
                    name="act2Crt"
                    value="1"
                    checked={String(invoiceData.act2Crt) === "1"}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <Input
                placeholder="ACT2 Copies Number"
                type="number"
                name="act2_copy_number"
                value={invoiceData.act2_copy_number}
                onChange={handleInputChange}
              />
              <ConfirmSection
                confirmed={confirmed.act2}
                onConfirm={() => confirmSection("act2")}
                label="Confirm ACT2 Data"
              />
            </>
          )}
        </div>
        <div className="mt-2 p-1 border rounded-lg">
          {invoiceData.act1 === true || invoiceData.act2 === true ? (
            <>
              <Input
                type="checkbox"
                placeholder="MOE Auth"
                name="moe_auth"
                required={false}
                onChange={handleInputChange}
                value={invoiceData.moe_auth}
              />
              {invoiceData.moe_auth && (
                <>
                  {invoiceData.act1 ? (
                    <>
                      <div className="flex justify-between">
                        <p className="p-2 pr-6">Moe ACT1 Number: </p>
                        <div className="flex">
                          <Input
                            placeholder="No Need"
                            type="radio"
                            name="moe1_number"
                            value="0"
                            checked={String(invoiceData.moe1_number) === "0"}
                            onChange={handleInputChange}
                          />
                          <Input
                            placeholder="Need One"
                            type="radio"
                            name="moe1_number"
                            value="1"
                            checked={String(invoiceData.moe1_number) === "1"}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      <Input
                        placeholder="MOE ACT1 Copies Number"
                        type="number"
                        name="moe1_copy_number"
                        value={invoiceData.moe1_copy_number}
                        onChange={handleInputChange}
                      />
                    </>
                  ) : null}
                  {invoiceData.act2 ? (
                    <>
                      <div className="flex justify-between">
                        <p className="p-2 pr-6">Moe ACT2 Number: </p>
                        <div className="flex">
                          <Input
                            placeholder="No Need"
                            type="radio"
                            name="moe2_number"
                            value="0"
                            checked={String(invoiceData.moe2_number) === "0"}
                            onChange={handleInputChange}
                          />
                          <Input
                            placeholder="Need One"
                            type="radio"
                            name="moe2_number"
                            value="1"
                            checked={String(invoiceData.moe2_number) === "1"}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      <Input
                        placeholder="MOE ACT2 Copies Number"
                        type="number"
                        name="moe2_copy_number"
                        value={invoiceData.moe2_copy_number}
                        onChange={handleInputChange}
                      />
                    </>
                  ) : null}
                  <ConfirmSection
                    confirmed={confirmed.moe}
                    onConfirm={() => confirmSection("moe")}
                    label="Confirm MOE Data"
                  />
                </>
              )}
            </>
          ) : (
            ""
          )}
        </div>
        {/* MOHE Statement — its own invoice (600 EGP per statement, every
            category); never part of the main invoice total. */}
        <div className="mt-2 p-1 border rounded-lg">
          <Input
            type="checkbox"
            placeholder="MOHE Statement (600 EGP, own invoice)"
            name="mohe_statement"
            required={false}
            onChange={handleInputChange}
            value={invoiceData.mohe_statement}
          />
          {invoiceData.mohe_statement && (
            <>
              <Input
                placeholder="Number of Statements"
                type="number"
                name="mohe_statement_count"
                value={invoiceData.mohe_statement_count}
                onChange={handleInputChange}
              />
              <ConfirmSection
                confirmed={confirmed.mohe_statement}
                onConfirm={() => confirmSection("mohe_statement")}
                label="Confirm MOHE Statement Data"
              />
            </>
          )}
        </div>
        <div className="mt-2 p-1 border rounded-lg">
        <Input
          type="checkbox"
          placeholder="Top Achievers"
          name="top_achievers"
          required={false}
          onChange={handleInputChange}
          value={invoiceData.top_achievers}
        />
        <Input
          type="checkbox"
          placeholder="Rush"
          name="rush"
          required={false}
          onChange={handleInputChange}
          value={invoiceData.rush}
        />
        <Input
          placeholder="Admin Fees"
          type="number"
          name="admin_fees"
          value={invoiceData.admin_fees}
          onChange={handleInputChange}
          float={true}
        />
        <Input
          placeholder="shipping"
          type="number"
          name="shipping"
          value={invoiceData.shipping}
          onChange={handleInputChange}
        />

        <Input
          placeholder="Discount"
          type="number"
          name="discount"
          value={invoiceData.discount}
          onChange={handleInputChange}
        />
        <div className="m-2 flex items-center justify-center">
          <label htmlFor="company_name" className="flex-auto">
            <span className="text-red-600">*</span>
            Company:
          </label>
          <select
            className="flex-initial p-2 ml-1 border rounded-xl focus:outline-blue-700"
            onChange={handleInputChange}
            name="company_name"
            value={invoiceData.company_name}
          >
            <option value="American Academy">American Academy</option>
            <option value="EDUVISION INTERNATIONAL">EduVision</option>
            <option value="Talent Track">Talent Track</option>
          </select>
        </div>
          <ConfirmSection
            confirmed={confirmed.fees}
            onConfirm={() => confirmSection("fees")}
            label="Confirm Fees & Company"
          />
        </div>
        {!allConfirmed && (
          <p className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
            Confirm every selected section above before creating the invoice.
          </p>
        )}
        <div className="inline-flex rounded-md shadow-sm text-sm lg:text-base md:text-base">
          <button
            className="py-2 px-3 text-gray-900 bg-white rounded-l-lg border
                     font-medium hover:text-blue-700 disabled:bg-gray-50
                     disabled:hover:text-gray-900 disabled:cursor-not-allowed"
            type="submit"
            disabled={loading || !allConfirmed}
          >
            {!loading ? "Create" : <Loading />}
          </button>
          <div className="flex gap-2">
  {/* Button for Download Main Invoice */}
  <button
    className="py-2 px-3 text-gray-900 bg-white rounded-lg border
             font-medium hover:text-blue-700 disabled:bg-gray-50
             disabled:hover:text-gray-900 disabled:cursor-not-allowed"
    disabled={!created}
  >
    <a
      href={`${url}/invoice/main/download/${id}`}
      target="_blank"
      onClick={() => {
        // Optional: You can add code here if needed
      }}
      rel="noreferrer"
    >
      Download Main
    </a>
  </button>

  {/* Button for Download Moe Invoice */}
  <button
    className="py-2 px-3 text-white bg-gray-500 rounded-lg
             font-medium hover:bg-blue-600 disabled:bg-gray-50
             disabled:hover:text-white disabled:cursor-not-allowed"
    disabled={!created || !invoiceData.moe_auth}
  >
    <a
      href={`${url}/invoice/moe/download/${id}`}
      target="_blank"
      onClick={(e) => {
        if (!created || !invoiceData.moe_auth) {
          e.preventDefault();
        }
      }}
      rel="noreferrer"
    >
      Download Moe
    </a>
  </button>

  {/* Button for Download MOHE Statement Invoice */}
  <button
    className="py-2 px-3 text-white bg-purple-700 rounded-lg
             font-medium hover:bg-purple-600 disabled:bg-gray-50
             disabled:hover:text-white disabled:cursor-not-allowed"
    disabled={!created || !invoiceData.mohe_statement}
  >
    <a
      href={`${url}/invoice/mohe_statement/download/${id}`}
      target="_blank"
      onClick={(e) => {
        if (!created || !invoiceData.mohe_statement) {
          e.preventDefault();
        }
      }}
      rel="noreferrer"
    >
      Download MOHE Statement
    </a>
  </button>

</div>

        </div>
      </form>
      <div className="p-2">
        <div className="p-3">
          <a
            className="py-2 px-3 text-gray-900 bg-white  rounded-l-lg border
                        font-medium hover:text-blue-700"
            href={`${url}/id_image/${id}`}
            target="_blank"
            rel="noreferrer"
          >
            Download ID Image
          </a>
        </div>
        <div className="p-3">
          <a
            className="py-2 px-3 text-gray-900 bg-white rounded-r-lg border
                    border-l-0 font-medium hover:text-blue-700"
            href={`${url}/letter_image/${id}`}
            target="_blank"
            rel="noreferrer"
          >
            Download Enrollment Image
          </a>
        </div>
      </div>
      <div className="flex  p-4 justify-around">
        <ActionButtonForward />
        <ActionButtons />
      </div>
    </div>
  );
}
